<template>
    <kt-modal
        :visible="true"
        title="Batch Add Parts"
        size="modal-lg"
        @dismiss="onCancel()"
    >
        <template #body>

            <!-- Staged items summary -->
            <div v-if="stagedCount > 0" class="alert alert-primary py-2 mb-3">
                <div class="d-flex align-items-center justify-content-between">
                    <span>
                        <i class="fas fa-boxes me-2"></i>
                        Adding <strong>{{ stagedCount }}</strong> part(s) to this job
                    </span>
                    <button class="btn btn-sm btn-outline-secondary" @click="clearStaged">
                        Clear All
                    </button>
                </div>
                <ul class="list-group list-group-flush mt-2">
                    <li
                        v-for="item in stagedList"
                        :key="item.part.Id"
                        class="list-group-item d-flex align-items-center justify-content-between py-1 px-2"
                    >
                        <span class="text-truncate me-2" :title="item.part.PartDescription">
                            {{ item.part.PartDescription }}
                            <small v-if="item.part.PartNumber" class="text-muted">({{ item.part.PartNumber }})</small>
                        </span>
                        <span class="d-flex align-items-center gap-2">
                            <span class="badge bg-secondary">x{{ item.qty }}</span>
                            <button
                                class="btn btn-sm btn-outline-danger"
                                title="Remove"
                                @click="unstagePart(item.part.Id)"
                            >
                                <i class="fa fa-times"></i>
                            </button>
                        </span>
                    </li>
                </ul>
            </div>

            <!-- Search -->
            <div class="mb-3">
                <label class="d-block">
                    <span class="form-label">Search Existing Parts</span>
                    <div class="input-group">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="searchQuery"
                            @input="onSearchInput"
                            placeholder="Search by description or part number..."
                        />
                        <button
                            class="btn btn-sm btn-outline-secondary"
                            @click="performSearch"
                            :disabled="!searchQuery || searchQuery.length < 2"
                        >
                            <i class="fa fa-search"></i>
                        </button>
                    </div>
                </label>
            </div>

            <div v-if="searchState.IsLoading" class="text-center py-3">
                <i class="fas fa-spinner fa-spin me-2"></i>Searching...
            </div>

            <div v-else-if="searchState.IsError" class="alert alert-danger py-2">
                {{ searchState.Message || 'Search failed' }}
            </div>

            <!-- Results -->
            <div v-else-if="searchResults.length > 0" class="table-responsive" style="max-height: 320px; overflow-y: auto;">
                <table class="table table-sm table-striped mb-0">
                    <thead>
                        <tr>
                            <th style="width: 90px;">QTY</th>
                            <th>Description</th>
                            <th>Part #</th>
                            <th class="text-end">MSRP</th>
                            <th class="text-end">Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="part in searchResults" :key="part.Id" :class="{ 'table-success': isStaged(part.Id) }">
                            <td>
                                <input
                                    type="number"
                                    class="form-control form-control-sm"
                                    min="0"
                                    step="1"
                                    :value="stagedQty(part.Id)"
                                    @input="onQtyInput(part, $event)"
                                    placeholder="0"
                                />
                            </td>
                            <td>
                                <span class="d-inline-block text-truncate" style="max-width: 260px;" :title="part.PartDescription">
                                    {{ part.PartDescription }}
                                </span>
                            </td>
                            <td>{{ part.PartNumber || 'N/A' }}</td>
                            <td class="text-end">${{ formatMoney(part.MSRP) }}</td>
                            <td class="text-end">${{ formatMoney(part.PartsCost) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else-if="searched" class="alert alert-info mb-0">
                No parts found matching "{{ searchQuery }}"
            </div>

        </template>

        <template #footer>
            <div class="kt-modal-actions">
                <button @click="onCancel()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Cancel
                </button>
                <button
                    class="btn btn-primary"
                    :disabled="stagedCount === 0 || submitState.IsLoading"
                    @click="submit()"
                >
                    <i v-if="submitState.IsLoading" class="fas fa-spinner fa-spin me-1"></i>
                    <PackagePlus v-else :size="16" class="me-1" />
                    Add {{ stagedCount }} Part(s)
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import Toast from "@/services/Toast";
import KtModal from "@/components/UI/KtModal.vue";
import { useApiRequest } from "@/composables";
import type { PartModel } from "@/models/PartModel";
import { X, PackagePlus } from "lucide-vue-next";

const props = defineProps({
    requestId: {
        type: [Number, String],
        required: true
    },
    techId: {
        type: [Number, String],
        default: null
    },
    workOrderId: {
        type: [Number, String],
        default: null
    }
});

const emit = defineEmits(["close", "added"]);

const searchQuery = ref("");
const searchResults = ref<PartModel[]>([]);
const searched = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

// Staged selections survive across searches: partId -> { part, qty }
const staged = ref<Map<number, { part: PartModel; qty: number }>>(new Map());

const { state: searchState, execute: runSearch } = useApiRequest<PartModel[]>("part:batch:search");
const { state: submitState, execute: runSubmit } = useApiRequest<PartModel[]>("part:batch:save");

const stagedCount = computed(() => staged.value.size);
const stagedList = computed(() => Array.from(staged.value.values()));

const isStaged = (id?: number | null) => id != null && staged.value.has(id);
const stagedQty = (id?: number | null) => (id != null && staged.value.get(id)?.qty) || "";

const onSearchInput = () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        if (searchQuery.value && searchQuery.value.length >= 2) {
            performSearch();
        } else {
            searchResults.value = [];
            searched.value = false;
        }
    }, 300);
};

const performSearch = async () => {
    if (!searchQuery.value || searchQuery.value.length < 2) return;

    const result = await runSearch(() => ApiService.searchParts(searchQuery.value));
    searchResults.value = (result.Data as PartModel[]) || [];
    searched.value = true;
};

const onQtyInput = (part: PartModel, event: Event) => {
    const qty = parseFloat((event.target as HTMLInputElement).value);
    if (part.Id == null) return;

    if (!isNaN(qty) && qty > 0) {
        staged.value.set(part.Id, { part, qty });
    } else {
        staged.value.delete(part.Id);
    }
    // Trigger reactivity for Map mutations
    staged.value = new Map(staged.value);
};

const unstagePart = (id?: number | null) => {
    if (id == null) return;
    staged.value.delete(id);
    staged.value = new Map(staged.value);
};

const clearStaged = () => {
    staged.value = new Map();
};

const submit = async () => {
    if (staged.value.size === 0) return;

    const payload = {
        RequestId: Number(props.requestId),
        TechId: props.techId ? Number(props.techId) : null,
        InitialRequest: props.workOrderId ? Number(props.workOrderId) : null,
        Items: stagedList.value.map((item) => ({
            SourcePartId: item.part.Id as number,
            Quantity: item.qty
        }))
    };

    const result = await runSubmit(() => ApiService.batchAddParts(payload));

    if (result.IsError) {
        Alert.error(result.Message || "Failed to add parts");
        return;
    }

    Toast.success(result.Message || `Added ${staged.value.size} part(s)`);
    emit("added");
    emit("close");
};

const onCancel = () => {
    emit("close");
};

const formatMoney = (value: any) => {
    if (value == null || value === "") return "0.00";
    return parseFloat(value).toFixed(2);
};
</script>
